import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchReferralsItemsByMemberID } from "./redux/slices/ReferralsThunks";
import ReferralCard from "./ReferralCard";
import { scrollToTop } from "./utils/func";

export default function Myreferrals() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const member = JSON.parse(localStorage.getItem("member"));
  const [filterType, setFilterType] = useState("");
  const [sortBy, setSortBy] = useState("latest");
  const [loading, setloading] = useState();

  const [tableData, setTableData] = useState([]);

  const formattedDate = (date) => {
    const newDate = new Date(date);
    const formatted = newDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return formatted;
  };

  const tableHeaders = [
    { label: "Referral ID", flex: true },
    { label: "Date", flex: true },
    { label: "Potential Client's Website", flex: true },
    { label: "Vendor", flex: true },
    { label: "Status", flex: true, type: "status" },
    { label: "Reward", flex: true, align: "flex-end" },
    { label: "Action", flex: true, align: "center" },
  ];

  const tableColumns = [
    { key: "referral_id", flex: true },
    { key: "date", flex: true },
    { key: "company_website_url", flex: true },
    { key: "vendor_name", flex: true },
    { key: "status", flex: true, type: "status" },
    { key: "vendor_reward", flex: true, align: "flex-end" },
    { key: "action", flex: true, align: "center" },
  ];

  const filteredReferrals = useMemo(() => {
    const statusOrder = ["Tracking", "Unsuccessful", "Successful", "Paid"];
    let filtered = [...tableData];

    // Filter by status
    if (statusOrder.includes(filterType)) {
      filtered = filtered.filter((item) => item?.status === filterType);
    }

    // ✅ Sort by date
    if (sortBy === "latest") {
      filtered = filtered.sort(
        (a, b) =>
          new Date(b?.referral.created_at) - new Date(a?.referral.created_at)
      );
    } else if (sortBy === "oldest") {
      filtered = filtered.sort(
        (a, b) =>
          new Date(a?.referral.created_at) - new Date(b?.referral.created_at)
      );
    }

    return filtered;
  }, [tableData, filterType, sortBy]);

  const [statusCounts, setStatusCounts] = useState({
    Successful: 0,
    Unsuccessful: 0,
    Tracking: 0,
    Paid: 0,
  });

  const getReferrals = async () => {
    try {
      setloading(true);

      const res = await dispatch(fetchReferralsItemsByMemberID(member._id));

      if (!res?.payload?.data?.length) {
        setTableData([]);
        setStatusCounts({
          Successful: 0,
          Unsuccessful: 0,
          Tracking: 0,
          Paid: 0,
        });
        return;
      }

      const { transformedData, statusCounts } = processReferralData(
        res.payload.data
      );

      setTableData(transformedData);
      setStatusCounts(statusCounts);
    } catch (error) {
      console.error("Failed to fetch referrals:", error);
      setTableData([]);
      setStatusCounts({ Successful: 0, Unsuccessful: 0, Tracking: 0, Paid: 0 });
    } finally {
      setloading(false);
    }
  };

  // Helper function to process referral data
  const processReferralData = (referrals) => {
    const statusCounts = {
      Successful: 0,
      Unsuccessful: 0,
      Tracking: 0,
      Paid: 0,
    };

    const transformedData = referrals.map((item) => {
      const referral = item;
      const status = referral?.referral_member_status || "";

      // Count statuses
      if (status && statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      }

      return {
        _id: item._id,
        referral_id: referral?._id ? `R${referral._id.slice(-7)}` : "",
        date: referral?.created_at ? formattedDate(referral.created_at) : "",
        status,
        industry_sector: referral?.industry_sector || "",
        company_website_url: referral?.company_website_url || "",
        vendor_name: referral?.vendor_id?.company_name || "",
        vendor: referral?.vendor_id || null,
        referral: referral || null,
        is_urgent: referral.is_urgent,
        vendor_reward: formatReward(referral?.reward),
        dispute: formatDispute(referral?.dispute),
      };
    });

    return { transformedData, statusCounts };
  };
  const formatReward = (reward) => {
    if (reward === undefined || reward === null) {
      return "-";
    }
    return `£${Number(reward).toLocaleString("en-GB")}`;
  };

  const formatDispute = (dispute) => {
    if (!dispute) return null;

    return {
      _id: dispute._id,
      dispute_status: dispute.dispute_status,
      explanation: dispute.explanation,
      supporting_document: dispute.supporting_document,
      created_at: dispute.date_raised ? formattedDate(dispute.date_raised) : "",
    };
  };

  useEffect(() => {
    getReferrals();
  }, []);

  const onViewReason = (item) => {
    navigate(`/myreferrals/${item._id}`, { state: { referral: item } });
    scrollToTop();
  };

  const downloadCSV = (columns, headers, data, filename = "referrals.csv") => {
    const csvHeaders = columns.map((col, index) => {
      let label = headers[index].label;
      // Change "Reward" to "Reward (GB)" for export
      if (label === "Reward") {
        label = "Reward (GBP)";
      }
      return label;
    });

    const csvRows = data.map((row) =>
      columns
        .map((col) => {
          let value = row[col.key] ?? "";

          // Special handling for reward field - send raw number only
          if (col.key === "vendor_reward" && value) {
            // Extract just the number from formatted values like "£1,500"
            value = value.toString().replace(/[£,]/g, "");
          }

          return `"${value}"`;
        })
        .join(",")
    );

    const csvString = [csvHeaders.join(","), ...csvRows].join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <RootWrapperMyreferralsone>
      <Dashboard>
        <Wrapper>
          <DashboardHeader>
            <Frame1462>
              <WelcomeJames>My Referrals</WelcomeJames>
              <KeepReferringAndEarningRewardsCheckYouStatsBelow>
                Keep referring and earning rewards. Check your stats below.
              </KeepReferringAndEarningRewardsCheckYouStatsBelow>
            </Frame1462>
            <Frame1461>
              <PRTNR.Button
                variant="black"
                onClick={() =>
                  downloadCSV(tableColumns, tableHeaders, filteredReferrals)
                }
              >
                Download Referrals
              </PRTNR.Button>
            </Frame1461>
          </DashboardHeader>

          <DashboardData>
            <Left_0001>
              <CardWrapper>
                <Frame1462_0001>
                  <Frame1431>
                    <Frame1431_0001>
                      <ReferralsDetails>Referrals Details</ReferralsDetails>
                    </Frame1431_0001>
                  </Frame1431>
                  <Frame1432>
                    <div style={{ minWidth: "147px" }}>
                      <PRTNR.Select
                        type="text"
                        placeholder="Filter by: "
                        options={[
                          { label: "Tracking", value: "Tracking" },
                          { label: "Unsuccessful", value: "Unsuccessful" },
                          { label: "Successful", value: "Successful" },
                          { label: "Paid", value: "Paid" },
                        ]}
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                      />
                    </div>
                    <div style={{ minWidth: "147px" }}>
                      <PRTNR.Select
                        placeholder="Sort by : [Latest]"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        options={[
                          {
                            value: "latest",
                            label: "Latest",
                          },
                          {
                            value: "oldest",
                            label: "Oldest",
                          },
                        ]}
                      />
                    </div>
                  </Frame1432>
                </Frame1462_0001>
                {filteredReferrals.map((item, index) => (
                  <ReferralCard key={index} item={item} />
                ))}
              </CardWrapper>

              <RecentReferrals>
                <Frame1462_0001>
                  <Frame1431>
                    <Frame1431_0001>
                      <ReferralsDetails>Referrals Details</ReferralsDetails>
                    </Frame1431_0001>
                  </Frame1431>
                  <Frame1432>
                    <div style={{ minWidth: "147px" }}>
                      <PRTNR.Select
                        type="text"
                        placeholder="Filter by: "
                        options={[
                          { label: "Tracking", value: "Tracking" },
                          { label: "Unsuccessful", value: "Unsuccessful" },
                          { label: "Successful", value: "Successful" },
                          { label: "Paid", value: "Paid" },
                        ]}
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                      />
                    </div>
                    <div style={{ minWidth: "147px" }}>
                      <PRTNR.Select
                        placeholder="Sort by : [Latest]"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        options={[
                          {
                            value: "latest",
                            label: "Latest",
                          },
                          {
                            value: "oldest",
                            label: "Oldest",
                          },
                        ]}
                      />
                    </div>
                  </Frame1432>
                </Frame1462_0001>

                <ScrollContainer>
                  <PRTNR.TableComponent
                    tableData={filteredReferrals}
                    tableHeaders={tableHeaders}
                    tableColumns={tableColumns}
                    onViewReason={onViewReason}
                    loading={loading}
                  />
                </ScrollContainer>
                <Frame1461_0001 />
              </RecentReferrals>
            </Left_0001>
          </DashboardData>
        </Wrapper>
      </Dashboard>
    </RootWrapperMyreferralsone>
  );
}

const RootWrapperMyreferralsone = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding-inline: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
  /* min-height: 966px; */
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0px 0px 32px;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 32px 0px;
  flex-shrink: 0;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 334px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(1.5rem, 5vw, 3rem);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: -1px;
  text-align: left;
`;

const KeepReferringAndEarningRewardsCheckYouStatsBelow = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(0.875rem, 2.5vw, 1.125rem);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1461 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const DashboardData = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 24px 0px;
  flex-shrink: 0;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
    flex: 1;
  }
`;

const RecentReferrals = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    display: none; /* Hide table on mobile */
  }
`;

const Frame1462_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Frame1431 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: 1;
  flex-wrap: wrap;
`;

const Frame1431_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  box-sizing: border-box;
  flex-wrap: wrap;
`;

const ReferralsDetails = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  line-height: 160%;
  text-align: left;
  min-height: 29px;
  flex: 1;
`;

const Frame1432 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;
const ScrollContainer = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const CardWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex; /* Show cards only on mobile */
    flex-direction: column;
    gap: 24px;
    width: 100%;
  }
`;

const Frame1461_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;
